/**
 * Privacy & Data Settings — barrister settings
 * Route: /barrister/settings/privacy
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BackButton } from '@/components/shared/BackButton';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  DownloadSimple, Database, Warning,
  User, CircleNotch, Info, UserMinus,
} from '@phosphor-icons/react';
import { api, getApiErrorMessage } from '@/lib/api';
import { useAuthStore } from '@/features/auth/store';

// ─── Section header ───────────────────────────────────────────

function SectionHead({ icon, title, desc }: { icon: React.ReactNode; title: string; desc?: string }) {
  return (
    <div className="mb-5 flex items-start gap-3 border-b border-border/50 pb-4">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:text-brand-400">
        {icon}
      </div>
      <div>
        <div className="text-sm font-semibold text-foreground">{title}</div>
        {desc && <div className="mt-0.5 text-xs text-muted-foreground">{desc}</div>}
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────

const MEMBER_LABELS: Record<string, string> = {
  silk:          "King's Counsel",
  junior:        'Junior Barrister',
  pupil:         'Pupil Barrister',
  door_tenant:   'Door Tenant',
  admin:         'Chambers Administrator',
  senior_clerk:  'Senior Clerk',
  junior_clerk:  'Junior Clerk',
  staff:         'Staff',
  solicitor:     'Solicitor',
};

// ══════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════

export default function PrivacySettingsPage() {
  const user = useAuthStore(s => s.user);

  const [downloading, setDownloading] = useState(false);

  const handleDataExport = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const res = await api.get('/me/data-export', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'jurivoxa-data-export.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Data export downloaded');
    } catch {
      toast.error('Failed to download data export');
    } finally {
      setDownloading(false);
    }
  };

  // Deactivate — accounts are never removed (CLAUDE.md #25). The SERVER decides how this person
  // confirms: their password, or — for an account that signs in only with Google/Microsoft — the
  // typed phrase. On success the server has already revoked every session, so this browser signs out.
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const clearAuth = useAuthStore(s => s.clear);
  const [confirmingDeactivation, setConfirmingDeactivation] = useState(false);
  const [deactivationInput, setDeactivationInput] = useState('');
  const [deactivating, setDeactivating] = useState(false);

  const confirmationQ = useQuery({
    queryKey: ['me', 'deactivation-confirmation'],
    queryFn: async () => {
      const res = await api.get('/me/deactivate');
      return res.data.data as { method: 'password' | 'typed'; phrase: string };
    },
    enabled: confirmingDeactivation,
  });
  const usesPhrase = confirmationQ.data?.method === 'typed';
  const phrase = confirmationQ.data?.phrase ?? 'DEACTIVATE';
  const canConfirmDeactivation = !!confirmationQ.data
    && (usesPhrase ? deactivationInput === phrase : deactivationInput.length > 0);

  const handleDeactivate = async () => {
    if (deactivating || !canConfirmDeactivation) return;
    setDeactivating(true);
    try {
      const body = usesPhrase ? { confirmation: deactivationInput } : { password: deactivationInput };
      await api.post('/me/deactivate', body);
      toast.success('Your account has been deactivated');
      clearAuth();
      queryClient.clear();
      navigate('/login', { replace: true });
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not deactivate your account'));
    } finally {
      setDeactivating(false);
      setDeactivationInput('');
    }
  };

  return (
    <div className="mx-auto max-w-2xl pb-20">
      {/* Reached from Settings but routed as a page of its own, so it inherits no back
          control from the Settings shell. History, not a fixed destination: the same page is
          opened from more than one place. */}
      <BackButton onClick={() => navigate(-1)} />

      <div className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Privacy &amp; Data</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Your account information, data rights under UK GDPR, and retention obligations.
        </p>
      </div>

      <div className="space-y-6">

        {/* Section A — Account Information */}
        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <SectionHead
            icon={<User size={14} weight="bold" />}
            title="Account Information"
            desc="Information held on your account record"
          />

          <div className="space-y-3">
            {[
              {
                label: 'Email address',
                value: user?.email ?? '—',
              },
              {
                label: 'Role',
                value: MEMBER_LABELS[user?.memberType ?? ''] ?? user?.memberType ?? '—',
              },
              {
                label: 'Account created',
                value: '—', // createdAt not in AuthUser — show placeholder
              },
            ].map(row => (
              <div
                key={row.label}
                className="flex items-center justify-between rounded-xl border border-border/50 px-4 py-3"
              >
                <span className="text-xs font-semibold text-muted-foreground">{row.label}</span>
                <span className="text-sm font-medium text-foreground">{row.value}</span>
              </div>
            ))}
          </div>

          <p className="mt-3.5 text-[10px] text-muted-foreground/70">
            To change your email address, contact your chambers administrator.
          </p>
        </div>

        {/* Section B — Data Export */}
        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <SectionHead
            icon={<DownloadSimple size={14} weight="bold" />}
            title="Data Export"
            desc="Your right to access under UK GDPR Article 15"
          />

          <p className="mb-4 text-sm text-muted-foreground leading-relaxed">
            Download a complete copy of your personal data held by this platform. This includes your
            profile, activity log, messages, and uploaded documents.
          </p>

          <button
            onClick={handleDataExport}
            disabled={downloading}
            className={`flex items-center gap-2 rounded-xl px-5 py-2.5 text-xs font-semibold transition-all ${
              downloading
                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                : 'bg-brand-600 text-white hover:bg-brand-700 shadow-sm'
            }`}
          >
            {downloading ? (
              <><CircleNotch size={13} className="animate-spin" />Preparing export…</>
            ) : (
              <><DownloadSimple size={13} weight="bold" />Download my data</>
            )}
          </button>
        </div>

        {/* Section C — Data Retention */}
        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <SectionHead
            icon={<Database size={14} weight="bold" />}
            title="Data Retention"
            desc="How long your data is kept and why"
          />

          <div className="space-y-3">
            <div className="flex items-start gap-3 rounded-xl border border-blue-100 bg-blue-50/40 dark:bg-blue-500/10 px-4 py-3.5">
              <Info size={15} className="mt-0.5 shrink-0 text-blue-600 dark:text-blue-400" weight="duotone" />
              <div className="text-xs text-blue-900 dark:text-blue-200 leading-relaxed">
                Matter records are retained for{' '}
                <strong>7 years after closure</strong> in compliance with BSB practice
                management obligations. Audit logs are retained for{' '}
                <strong>5 years</strong>.
              </div>
            </div>

            <p className="text-xs text-muted-foreground leading-relaxed">
              These retention periods are mandated by the Bar Standards Board and HMRC requirements.
              Data retained for legal compliance purposes cannot be deleted on request.
            </p>
          </div>
        </div>

        {/* Section D — Deactivate account (accounts are never removed — CLAUDE.md #25) */}
        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <SectionHead
            icon={<UserMinus size={14} weight="bold" />}
            title="Deactivate account"
            desc="Switch off your access to Jurivoxa"
          />

          <div className="mb-4 flex items-start gap-3 rounded-xl border border-amber-200/70 dark:border-amber-500/30 bg-amber-50/40 dark:bg-amber-500/10 px-4 py-3.5">
            <Warning size={15} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" weight="duotone" />
            <div className="space-y-1.5 text-xs text-amber-900 dark:text-amber-200 leading-relaxed">
              <p>
                Deactivating your account signs you out everywhere and stops you signing in. Your account
                and everything linked to it — matters, documents, communications, financial records and
                history — are kept.
              </p>
              <p>
                You cannot reactivate the account yourself. To regain access, contact your chambers
                administrator or Jurivoxa support.
              </p>
              <p>Deactivating your account is not a request to erase your personal data.</p>
            </div>
          </div>

          {!confirmingDeactivation ? (
            <button
              onClick={() => setConfirmingDeactivation(true)}
              className="flex items-center gap-2 rounded-xl border border-amber-300 dark:border-amber-500/40 px-5 py-2.5 text-xs font-semibold text-amber-700 dark:text-amber-300 transition hover:bg-amber-50 dark:hover:bg-amber-500/10"
            >
              <UserMinus size={13} weight="bold" />
              Deactivate my account
            </button>
          ) : (
            <div className="space-y-3">
              {confirmationQ.isLoading ? (
                <CircleNotch size={16} className="animate-spin text-muted-foreground" />
              ) : (
                <div>
                  <label htmlFor="deactivation-confirm" className="mb-1.5 block text-xs font-medium text-foreground">
                    {usesPhrase ? <>Type <span className="font-semibold">{phrase}</span> to confirm</> : 'Enter your password to confirm'}
                  </label>
                  <input
                    id="deactivation-confirm"
                    type={usesPhrase ? 'text' : 'password'}
                    autoComplete={usesPhrase ? 'off' : 'current-password'}
                    value={deactivationInput}
                    onChange={(e) => setDeactivationInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') void handleDeactivate(); }}
                    className="h-9 w-full max-w-sm rounded-lg border border-border bg-background px-3 text-sm text-foreground focus:border-amber-500 focus:outline-none"
                  />
                </div>
              )}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => void handleDeactivate()}
                  disabled={!canConfirmDeactivation || deactivating}
                  className="flex items-center gap-2 rounded-xl bg-amber-600 px-5 py-2.5 text-xs font-semibold text-white transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {deactivating ? <CircleNotch size={13} className="animate-spin" /> : <UserMinus size={13} weight="bold" />}
                  Confirm deactivation
                </button>
                <button
                  onClick={() => { setConfirmingDeactivation(false); setDeactivationInput(''); }}
                  disabled={deactivating}
                  className="rounded-xl border border-border px-5 py-2.5 text-xs font-medium text-muted-foreground transition hover:bg-muted"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
